import { useCallback, useEffect, useRef, useState } from 'react';
import { api, imageUrl } from '../services/api';
import { clearProductosCache } from '../services/realtime';
import { warmProductImages } from '../utils/imageCache';

/** Páginas grandes = menos viajes al API en categorías con muchos productos. */
const PAGE_SIZE = 80;
const CACHE_KEY = 'oh_productos_cache_v5';

function mergeProductos(prev, next, append) {
  if (!append) return next || [];
  const seen = new Set(prev.map((p) => String(p.codigo)));
  const merged = [...prev];
  for (const p of next || []) {
    const code = String(p.codigo);
    if (seen.has(code)) continue;
    seen.add(code);
    merged.push(p);
  }
  return merged;
}

function cacheKey(q, categoria) {
  return `${String(q || '').trim().toLowerCase()}||${categoria || 'Todas'}`;
}

function readCache(q, categoria) {
  try {
    const all = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
    const hit = all[cacheKey(q, categoria)];
    if (hit?.productos?.length) return hit;
    if (q || (categoria && categoria !== 'Todas')) {
      const base = all[cacheKey('', 'Todas')];
      if (base?.productos?.length) return { ...base, fromFallback: true };
    }
    return null;
  } catch {
    return null;
  }
}

function writeCache(q, categoria, productos, total) {
  try {
    const all = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}');
    all[cacheKey(q, categoria)] = {
      productos: (productos || []).slice(0, 400),
      total,
      at: Date.now(),
    };
    const keys = Object.keys(all);
    if (keys.length > 24) {
      keys
        .map((k) => ({ k, at: all[k]?.at || 0 }))
        .sort((a, b) => a.at - b.at)
        .slice(0, keys.length - 24)
        .forEach(({ k }) => {
          delete all[k];
        });
    }
    localStorage.setItem(CACHE_KEY, JSON.stringify(all));
  } catch {
    /* quota */
  }
}

function filterCached(list, q, categoria) {
  const qq = String(q || '').trim().toLowerCase();
  const isSub = categoria && categoria !== 'Todas';
  const want = String(categoria || '').toLowerCase();
  const aliases =
    want === 'cabello' || want === 'capilar' ? ['cabello', 'capilar'] : [want];
  return (list || []).filter((p) => {
    if (isSub) {
      const sub = String(p.subcategoria || p.categoria || '').toLowerCase();
      if (!aliases.some((a) => sub.includes(a))) return false;
    }
    if (!qq) return true;
    const hay = `${p.codigo || ''} ${p.nombre || ''}`.toLowerCase();
    return hay.includes(qq);
  });
}

function paintFromCache(cached, q, categoria) {
  if (!cached?.productos?.length) return null;
  let list = cached.productos;
  if (cached.fromFallback) list = filterCached(list, q, categoria);
  if (!list.length) return null;
  return {
    list,
    total: cached.fromFallback ? list.length : cached.total || list.length,
    fromFallback: Boolean(cached.fromFallback),
  };
}

export default function usePaginatedProductos({
  q = '',
  categoria = 'Todas',
  onError,
  paused = false,
} = {}) {
  const [productos, setProductos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(!paused);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [fromCache, setFromCache] = useState(false);
  const offsetRef = useRef(0);
  const onErrorRef = useRef(onError);
  const reqIdRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const productosRef = useRef([]);
  const offlineToastShownRef = useRef(false);
  const prefetchingRef = useRef(false);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    productosRef.current = productos;
  }, [productos]);

  const applyCacheFallback = useCallback(
    (message) => {
      const painted = paintFromCache(readCache(q, categoria), q, categoria);
      if (!painted) {
        onErrorRef.current?.(message, 'error');
        return false;
      }
      setProductos(painted.list);
      setTotal(painted.total);
      setHasMore(false);
      setFromCache(true);
      offsetRef.current = painted.list.length;
      if (!offlineToastShownRef.current) {
        offlineToastShownRef.current = true;
        onErrorRef.current?.('Sin internet — mostrando productos guardados en la tablet', 'ok');
      }
      return true;
    },
    [q, categoria]
  );

  const fetchPage = useCallback(
    async (offset, append, reqId) => {
      const isSub = categoria !== 'Todas';
      const res = await api.getProductos({
        q,
        subcategoria: isSub ? categoria : '',
        offset,
        limit: PAGE_SIZE,
      });
      if (reqId !== reqIdRef.current) return null;
      const data = res.data || [];
      const grandTotal = Number(res.total) || 0;
      setTotal(grandTotal);
      setProductos((prev) => {
        const next = mergeProductos(prev, data, append);
        writeCache(q, categoria, next, grandTotal);
        return next;
      });
      setFromCache(false);
      offlineToastShownRef.current = false;
      offsetRef.current = offset + data.length;
      const more = offsetRef.current < grandTotal && data.length > 0;
      setHasMore(more);
      warmProductImages(
        data.map((p) => imageUrl(p.imagen)).filter(Boolean),
        { limit: 120 }
      );
      return { data, more, nextOffset: offset + data.length, grandTotal };
    },
    [q, categoria]
  );

  const reload = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    offsetRef.current = 0;

    // Pintar YA lo cacheado (cambio de categoría casi al instante).
    const painted = paintFromCache(readCache(q, categoria), q, categoria);
    if (painted) {
      setProductos(painted.list);
      setTotal(painted.total);
      offsetRef.current = painted.list.length;
      setFromCache(true);
      setHasMore(!painted.fromFallback);
      setLoading(false);
      warmProductImages(
        painted.list.map((p) => imageUrl(p.imagen)).filter(Boolean),
        { limit: 200 }
      );
    } else {
      setProductos([]);
      setTotal(0);
      setHasMore(false);
      setFromCache(false);
      setLoading(true);
    }

    try {
      const first = await fetchPage(0, false, reqId);
      if (!first || reqId !== reqIdRef.current) return;
      // Prefetch 2ª página en silencio (categorías grandes se sienten instantáneas al hacer scroll).
      if (first.more && !prefetchingRef.current) {
        prefetchingRef.current = true;
        fetchPage(first.nextOffset, true, reqId)
          .catch(() => {})
          .finally(() => {
            prefetchingRef.current = false;
          });
      }
    } catch (err) {
      if (reqId !== reqIdRef.current) return;
      if (productosRef.current.length) {
        setFromCache(true);
        if (!offlineToastShownRef.current) {
          offlineToastShownRef.current = true;
          onErrorRef.current?.('Sin internet — se mantienen los productos ya cargados', 'ok');
        }
      } else if (!applyCacheFallback(err.message)) {
        setProductos([]);
        setTotal(0);
        setHasMore(false);
      }
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  }, [fetchPage, applyCacheFallback, q, categoria]);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || loading || !hasMore) return;
    // Si solo hay fallback genérico sin red, no pedir más.
    if (fromCache && !productosRef.current.length) return;
    loadingMoreRef.current = true;
    const reqId = reqIdRef.current;
    setLoadingMore(true);
    try {
      await fetchPage(offsetRef.current, true, reqId);
    } catch (err) {
      if (reqId === reqIdRef.current) onErrorRef.current?.(err.message, 'error');
    } finally {
      loadingMoreRef.current = false;
      if (reqId === reqIdRef.current) setLoadingMore(false);
    }
  }, [fetchPage, hasMore, loading, fromCache]);

  useEffect(() => {
    if (paused) {
      reqIdRef.current += 1;
      setLoading(false);
      setLoadingMore(false);
      return undefined;
    }
    reload();
    return undefined;
  }, [reload, paused]);

  // Nunca dejar “Cargando…” eterno en la tablet.
  useEffect(() => {
    if (!loading) return undefined;
    const t = setTimeout(() => {
      setLoading(false);
      if (!productosRef.current.length) {
        const painted = paintFromCache(readCache(q, categoria), q, categoria);
        if (painted) {
          setProductos(painted.list);
          setTotal(painted.total);
          setFromCache(true);
          onErrorRef.current?.('Sin respuesta del servidor — se muestran productos guardados', 'ok');
        } else {
          onErrorRef.current?.('La carga tardó demasiado. Revisa el WiFi e intenta de nuevo.', 'error');
        }
      }
    }, 22000);
    return () => clearTimeout(t);
  }, [loading, q, categoria]);

  const softRefresh = useCallback(async () => {
    if (paused) return;
    const keep = Math.max(PAGE_SIZE, productosRef.current.length);
    const reqId = ++reqIdRef.current;
    const isSub = categoria !== 'Todas';
    try {
      const res = await api.getProductos({
        q,
        subcategoria: isSub ? categoria : '',
        offset: 0,
        limit: keep,
      });
      if (reqId !== reqIdRef.current) return;
      const data = res.data || [];
      const grandTotal = Number(res.total) || 0;
      setTotal(grandTotal);
      setProductos(data);
      writeCache(q, categoria, data, grandTotal);
      offsetRef.current = data.length;
      setHasMore(data.length < grandTotal && data.length > 0);
      setFromCache(false);
    } catch {
      /* se queda la lista actual; no volver al inicio */
    }
  }, [q, categoria, paused]);

  useEffect(() => {
    let t;
    const onInv = (ev) => {
      const detail = ev.detail || {};
      const domains = detail.domains || [];
      if (!domains.includes('product') && !domains.includes('inventory')) return;
      const deleted = detail.reason === 'producto_eliminado' && detail.codigo;
      if (deleted) {
        const codigo = String(detail.codigo);
        setProductos((prev) => prev.filter((p) => String(p.codigo) !== codigo));
        setTotal((t0) => Math.max(0, t0 - 1));
        clearProductosCache();
      }
      clearTimeout(t);
      t = setTimeout(() => {
        if (deleted) reload();
        else softRefresh();
      }, deleted ? 120 : 350);
    };
    window.addEventListener('oh:invalidate', onInv);
    return () => {
      clearTimeout(t);
      window.removeEventListener('oh:invalidate', onInv);
    };
  }, [softRefresh, reload]);

  return {
    productos,
    total,
    loading,
    loadingMore,
    hasMore,
    fromCache,
    loadMore,
    reload,
    shown: productos.length,
  };
}
